import React, { useEffect } from "react";
import dayjs from "dayjs";
import ReactDatePicker from "react-datepicker";
import { BOLD_TEXT, COLOR_BORDER, COLOR_TEXT, SIZE_TEXT } from "@/comn/features/foundation";
import { comnUtils } from "@/comn/utils";
import constants from "@/comn/constants";
import { useTheme } from "@/comn/hooks";
import { Button, Icon } from "@/comn/components";
import classNames from "classnames";
import { useTranslation } from "react-i18next";

export type InputDateProps = {
    edit?: boolean;
    mode?: "edit" | "view" | null;
    startRef?: any;
    endRef?: any;
    maxDate?: Date;
    minDate?: Date;

    name?: string;
    value?: any;
    readOnly?: boolean;
    disabled?: boolean;
    defaultValue?: any;
    placeholder?: string;

    color?: keyof typeof COLOR_TEXT;
    editColor?: keyof typeof COLOR_TEXT;
    borderColor?: keyof typeof COLOR_BORDER;
    bold?: keyof typeof BOLD_TEXT;
    editBold?: keyof typeof BOLD_TEXT;
    fontSize?: keyof typeof SIZE_TEXT;

    onBlur?: (arg?: any) => void;
    onChange?: (arg?: any) => void;
};

export const InputDate = React.forwardRef((props: InputDateProps, ref: any) => {
    const {
        edit = true,
        mode,
        startRef,
        endRef,
        maxDate = new Date("9999-12-31"),
        minDate = new Date("1800-01-01"),
        /** input props */
        name,
        value,
        readOnly,
        disabled,
        defaultValue,
        placeholder,

        color,
        editColor,
        borderColor,
        bold,
        editBold,
        fontSize,

        onBlur,
        onChange,
    } = props;
    const _props = Object.fromEntries(
        Object.entries({
            maxDate,
            minDate,
            name,
            readOnly,
            disabled,
            defaultValue,
            onBlur,
        }).filter(([, value]) => value !== undefined),
    );
    const { theme } = useTheme();
    const { t } = useTranslation();

    useEffect(() => {
        if (startRef) startRef.current.handleChangeStart = handleChange;
        if (endRef) endRef.current.handleChangeEnd = handleChange;
    }, []);

    const handleChange = (date: Date) => {
        if (onChange) {
            onChange(unformatDate(date) || null);
        }
    };

    const EDIT_STATUS = mode === "edit" ? true : mode === "view" ? false : edit;
    const _value = formatDate(value);

    return (
        <div className="w-full">
            {!EDIT_STATUS && (
                <div
                    className={classNames(
                        "control-text w-fit min-w-max",
                        comnUtils.getViewStyle(color, bold, fontSize),
                    )}
                >
                    {localeDate(_value, theme.lang)}
                </div>
            )}
            <div hidden={!EDIT_STATUS}>
                <div className="relative w-full [&>div]:w-full">
                    <Icon icon="calendar" size="xs" className="absolute left-1 top-1/2 -translate-y-1/2 z-[1]" />
                    <ReactDatePicker
                        {..._props}
                        ref={(context: any) => {
                            if (context) {
                                if (typeof ref === "function") {
                                    ref(context.input);
                                }
                            }
                        }}
                        placeholderText={placeholder}
                        dateFormat={constants.DATE_FORMAT_INPUT[theme.lang]}
                        selected={_value}
                        onChange={handleChange}
                        autoComplete="off"
                        className={classNames(
                            "input pl-5" + comnUtils.getEditStyle(editColor, editBold),
                            borderColor ? COLOR_BORDER[borderColor] : "",
                        )}
                        portalId="datepicker"
                        popperProps={{ strategy: "fixed" }}
                        showYearDropdown={true}
                        scrollableYearDropdown={true}
                        yearDropdownItemNumber={80}
                        dropdownMode="scroll"
                        showMonthDropdown={true}
                        todayButton={
                            <div className="flex justify-center items-center">
                                <Button>{t("L_TODAY")}</Button>
                            </div>
                        }
                    />
                </div>
            </div>
        </div>
    );
});

export const localeDate = (v: any, l: "ko" | "en" | "tz") => {
    if (!v) return "";
    if (!dayjs(v).isValid()) return "";

    return dayjs(v).format(constants.DATE_FORMAT_DAYJS[l]);
};

export const formatDate = (v: any) => {
    if (!v) return null;
    if (!dayjs(v).isValid()) return null;

    return dayjs(v).toDate();
};

export const unformatDate = (v: any, o?: any) => {
    if (!v) return undefined;
    if (!dayjs(v).isValid()) return undefined;

    return dayjs(v).format(constants.DATE_FORMAT);
};
